import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { uploadTorDocument, updateTorSpecification, getTorDocument, deleteTorDocument, downloadTorDocument, TorUploadRequest, TorSpecificationRequest } from '../api/torDocuments';
import { notifications } from '@mantine/notifications';

// Upload TOR document
export const useUploadTorDocument = (tenderId: string) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: TorUploadRequest) => uploadTorDocument(tenderId, data),
    onSuccess: () => {
      notifications.show({
        title: 'Success',
        message: 'TOR document uploaded successfully',
        color: 'green',
      });
      // Invalidate tender data to refresh
      queryClient.invalidateQueries({ queryKey: ['tender', tenderId] });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to upload TOR document',
        color: 'red',
      });
    },
  });
};

// Update TOR specification
export const useUpdateTorSpecification = (tenderId: string) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: TorSpecificationRequest) => updateTorSpecification(tenderId, data),
    onSuccess: () => {
      notifications.show({
        title: 'Success',
        message: 'TOR specification updated successfully',
        color: 'green',
      });
      // Invalidate tender data to refresh
      queryClient.invalidateQueries({ queryKey: ['tender', tenderId] });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to update TOR specification',
        color: 'red',
      });
    },
  });
};

// Get TOR document
export const useGetTorDocument = (tenderId: string) => {
  return useQuery({
    queryKey: ['torDocument', tenderId],
    queryFn: () => getTorDocument(tenderId),
    enabled: !!tenderId,
    retry: false, // Don't retry if document doesn't exist
    staleTime: 0, // Always consider data stale to ensure fresh data
  });
};

// Delete TOR document
export const useDeleteTorDocument = (tenderId: string) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: () => deleteTorDocument(tenderId),
    onSuccess: () => {
      notifications.show({
        title: 'Success',
        message: 'TOR document deleted successfully',
        color: 'green',
      });
      // Remove the cached TOR document data and invalidate queries
      queryClient.removeQueries({ queryKey: ['torDocument', tenderId] });
      queryClient.invalidateQueries({ queryKey: ['tender', tenderId] });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to delete TOR document',
        color: 'red',
      });
    },
  });
};

// Download TOR document
export const useDownloadTorDocument = (tenderId: string) => {
  const { data: torDocument } = useGetTorDocument(tenderId);
  
  return useMutation({
    mutationFn: async () => {
      const response = await downloadTorDocument(tenderId);
      return response;
    },
    onSuccess: (blob: Blob) => {
      // Get filename from TOR document info or use default
      let filename = `tor-document-${tenderId}.pdf`;
      if (torDocument?.fileName) {
        filename = torDocument.fileName;
      }
      
      // Create a download link and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      notifications.show({
        title: 'Success',
        message: 'TOR document downloaded successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to download TOR document',
        color: 'red',
      });
    },
  });
}; 